import {
  type CalculatedField as FieldDefinition,
  FieldType,
  type TextField as TextFieldDefinition,
  TextInputType,
} from '@bybeam/platform-types';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import {
  GenericFormData,
  fieldUpdate,
  updateRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import { evaluate } from '@platform-ui/utilities/application/ast/evaluate.js';
import { useEffect } from 'react';
import TextField from './TextField.js';
import { FieldProps } from './types.js';

export default function CalculatedField<FormData extends GenericFormData>({
  field,
  field: { key, display, formula },
  dispatch,
  answers,
  repeated,
  setValidationOverrides: _,
  ...rest
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  const application = useApplication();

  // This useEffect saves the value of the calculated field
  // For now only TextField component is rendering a CalculatedField
  useEffect(() => {
    // The question page only passes this question's form data as `answers`, but a
    // formula may reference inputs from other questions (e.g. utility allowances
    // computed from values in a separate question). Evaluate against the full
    // application answers, with the live form data layered on top so in-progress
    // edits win. Repeated fields stay item-scoped to preserve their behavior.
    const evaluationAnswers = repeated ? answers : { ...application?.answers, ...answers };
    const newResult = evaluate(formula, evaluationAnswers);
    if (key && formula && answers[key] !== newResult) {
      if (repeated) {
        dispatch(updateRepeated(repeated.parentKey, repeated.index, key, newResult));
      } else {
        dispatch(fieldUpdate(key, newResult as FormData[typeof key]));
      }
    }
    // Re-run when the stored application answers refresh (e.g. after a save) so
    // cross-question inputs stay current. Safe from loops: dispatch only fires
    // when the computed value actually differs from the current form value.
  }, [answers, application?.answers, key, formula, repeated, dispatch]);

  return (
    <TextField
      dispatch={dispatch}
      field={{
        type: FieldType.Text,
        key: key,
        copy: display.copy,
        validation: display.validation,
        props: { disabled: true } as TextFieldDefinition['props'],
        inputType: display.inputType ?? TextInputType.Text,
      }}
      answers={answers}
      repeated={repeated}
      {...rest}
    />
  );
}
